/**
 * Paginación cliente para listas largas.
 *
 * Con ~350 personas, varias tablas de People montaban TODAS las filas de una
 * (el community map son 350 × 19 columnas = ~6.650 celdas): la página tardaba
 * en abrir y el listado "se veía eterno". El scroll con `overflow-y-auto` no
 * alcanza — recorta visualmente pero monta todo igual.
 *
 * Devuelve la página actual + un pie listo para renderizar. Resetea a la
 * primera página cuando cambia el total (o sea, cuando cambian los filtros) o
 * cuando cambia `resetKey` (un filtro que deja el mismo total, p. ej. cambiar
 * de un área a otra del mismo tamaño).
 */
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export function usePagination<T>(items: T[], pageSize = 25, resetKey?: string) {
  const [page, setPage] = useState(0);
  const total = items.length;

  // Cambió el filtro (o llegaron datos nuevos) ⇒ volver al principio
  useEffect(() => { setPage(0); }, [total, resetKey]);

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pages - 1);
  const pageItems = useMemo(
    () => items.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [items, safePage, pageSize],
  );

  const { t } = useTranslation();
  const footer = total > pageSize ? (
    <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
      <span>
        {t('common.pagination.showing', { from: safePage * pageSize + 1, to: Math.min((safePage + 1) * pageSize, total), total })}
      </span>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" aria-label={t('common.a11y.previous_page')} className="h-7 w-7" disabled={safePage === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="px-1">{safePage + 1} / {pages}</span>
        <Button variant="ghost" size="icon" aria-label={t('common.a11y.next_page')} className="h-7 w-7" disabled={safePage >= pages - 1}
                onClick={() => setPage(p => Math.min(pages - 1, p + 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  ) : null;

  return { pageItems, footer, page: safePage, setPage, pages, total };
}
